import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import icon_save from '../icon_save.svg';
import icon_bell from '../icon_bell.svg';

const Tab = React.memo(({ item, isActive, onClick, Tasks, isHidde = false, time = false }) => {
  const isNews = item.data?.news;
  const isSaved = Tasks.some(el => el.uid === item.data?.uid);

  const renderContent = () => {
    if (item.wndType !== 'orderTab' || !isHidde) {
      return (
        <>
          {isSaved ? <img src={icon_save} alt='' /> : isNews ? <img src={icon_bell} alt='' /> : null}
          {item.caption} {time && `- ${time}`}
        </>
      );
    }

    if (item.data?.docType) {
      return (
        <>
          <div style={{ display: 'inline-flex', width: '260px', overflow: 'hidden' }}>
            {isSaved ? <img src={icon_save} alt='' /> : isNews ? <img src={icon_bell} alt='' /> : null}
            {(item.data.address || '') + ' ' + (item.data.phone || '') + ' ' + (item.data.name || '')} {time && `- ${time}`}
          </div>
          <div style={{ display: 'inline-flex', width: '50px', justifyContent: 'flex-end' }}>
            <b>{(item.data?.sum || 0).toFixed(2)}</b>
          </div>
        </>
      );
    }

    return (
      <>
        <div style={{ display: 'inline-flex', width: '260px', overflow: 'hidden' }}>
          {isSaved ? <img src={icon_save} alt='' /> : isNews ? <img src={icon_bell} alt='' /> : null}
          {item.caption} {time && `- ${time}`}
        </div>
        <div style={{ display: 'inline-flex', width: '50px', justifyContent: 'flex-end' }}>
          <b>{(item.data?.sum || 0).toFixed(2)}</b>
        </div>
      </>
    );
  };

  return (
    <div
      tabIndex='0'
      className={`${isHidde ? 'hidde-tab' : 'tabbuton'} ${isActive ? 'active' : ''}`}
      role='button'
      onKeyUp={e => { if (e.key === 'Enter') onClick(); }}
      onClick={onClick}
    >
      {renderContent()}
    </div>
  );
});

function SimpleTabPanel({ Tabs, Tasks, ActiveTab, TabClick }) {
  return (
    <div className='tabpanel'>
      {Tabs.map((item, index) => (
        <Tab
          key={`${item.wndType}-${item.data?.uid || index}`}
          item={item}
          isActive={index === ActiveTab}
          onClick={() => TabClick(index)}
          Tasks={Tasks}
          isHidde={false}
        />
      ))}
    </div>
  );
}

function AdvancedTabPanel({ Tabs, Tasks, User, ActiveTab, TabClick }) {
  const [hiddenOrders, setHiddenOrders] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const resizeTimeoutRef = useRef(null);

  const getCategorizedOrders = useCallback((orders) => {
    const inRestaurant = orders.filter(order =>
      order.wndType === 'orderTab' &&
      (!order.data.address || order.data.address === null) &&
      order.data.docType === 0
    );

    const delivery = orders.filter(order =>
      order.wndType === 'orderTab' &&
      order.data.docType === 1
    );

    const pickup = orders.filter(order =>
      order.wndType === 'orderTab' &&
      order.data.docType === 2
    );

    return { inRestaurant, delivery, pickup };
  }, []);

  const categorizedOrders = useMemo(() => getCategorizedOrders(hiddenOrders), [hiddenOrders, getCategorizedOrders]);

  const handleTabClick = useCallback((index) => {
    TabClick(index);
    setShowDropdown(false);
  }, [TabClick]);

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        containerRef.current && !containerRef.current.contains(event.target)) {
      setShowDropdown(false);
    }
  }, []);

  const updateVisibleTabs = useCallback(() => {
    if (!containerRef.current) return;

    const containerWidth = containerRef.current.clientWidth;
    let visibleTabs = [];
    let overflowTabs = [];
    let widthUsed = 0;

    const fixedTabs = Tabs.filter(item => ['tablesTab', 'deliveryTab', 'banquetTab', 'pickupTab'].includes(item.wndType));
    const orderTabs = Tabs.filter(item => item.wndType === 'orderTab');

    fixedTabs.forEach(item => {
      visibleTabs.push(item);
      widthUsed += 120;
    });

    const lastUserOrder = orderTabs
      .filter(tab => tab.wndType === 'orderTab' && tab.data.author?.id === User)
      .sort((a, b) => new Date(b.data.last_upd) - new Date(a.data.last_upd))[0];

    const activeTab = Tabs[ActiveTab];
    if (activeTab && activeTab.wndType === 'orderTab' && !visibleTabs.includes(activeTab)) {
      visibleTabs.push(activeTab);
      widthUsed += 120;
    }
    
    const shouldShowLastOrder = lastUserOrder &&
      (!activeTab || activeTab.wndType !== 'orderTab' || activeTab.data.uid === lastUserOrder.data.uid) &&
      !visibleTabs.some(tab => tab.data.uid === lastUserOrder.data.uid);

    if (shouldShowLastOrder) {
      visibleTabs.push(lastUserOrder);
      widthUsed += 120;
    }

    orderTabs.forEach(item => {
      if (!visibleTabs.includes(item)) {
        if (widthUsed + 120 <= containerWidth - 50) {
          visibleTabs.push(item);
          widthUsed += 120;
        } else {
          overflowTabs.push(item);
        }
      }
    });

    setHiddenOrders(prev => {
      if (prev.length !== overflowTabs.length ||
          !prev.every((val, index) => val === overflowTabs[index])) {
        return overflowTabs;
      }
      return prev;
    });
  }, [Tabs, ActiveTab, User]);

  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        updateVisibleTabs();
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleClickOutside);

    updateVisibleTabs();

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [updateVisibleTabs, handleClickOutside]);

  const visibleTabs = useMemo(() => {
    return Tabs.filter(item =>
      ['tablesTab', 'deliveryTab', 'banquetTab', 'pickupTab'].includes(item.wndType) ||
      item === Tabs[ActiveTab] ||
      !hiddenOrders.includes(item)
    );
  }, [Tabs, ActiveTab, hiddenOrders]);

  return (
    <div className='tabpanel' ref={containerRef}>
      {visibleTabs.map((item, index) => (
        <Tab
          key={`${item.wndType}-${item.data?.uid || index}`}
          item={item}
          isActive={item === Tabs[ActiveTab]}
          onClick={() => handleTabClick(Tabs.indexOf(item))}
          Tasks={Tasks}
        />
      ))}

      {hiddenOrders.length > 0 && (
        <div
          className='toolBarItem'
          onClick={() => setShowDropdown(!showDropdown)}
          style={{ border: 'none', background: 'none' }}
        >
          <div className='navbarMenu'></div>
        </div>
      )}

      {showDropdown && (
        <div
          className='list'
          ref={dropdownRef}
          style={{
            position: 'absolute',
            overflow: 'auto',
            right: 0,
            top: '30px',
            whiteSpace: 'nowrap',
            fontWeight: 'normal'
          }}
        >
          {categorizedOrders.inRestaurant.length > 0 && (
            <>
              <div className='dropdown-header'>В закладі</div>
              {categorizedOrders.inRestaurant.map((item) => {
                const timeString = new Date(item.data.open).toLocaleTimeString('ru-RU', {
                  hour: '2-digit',
                  minute: '2-digit'
                });
                return (
                  <Tab
                    key={`in-${item.data.uid}`}
                    item={item}
                    isActive={false}
                    onClick={() => handleTabClick(Tabs.indexOf(item))}
                    Tasks={Tasks}
                    isHidde={true}
                    time={timeString}
                  />
                );
              })}
            </>
          )}

          {categorizedOrders.delivery.length > 0 && (
            <>
              <div className='dropdown-header'>Доставка</div>
              {categorizedOrders.delivery.map((item) => {
                const timeString = (new Date(item.data.open) ?? new Date()).toLocaleTimeString('ru-RU', {
                  hour: '2-digit',
                  minute: '2-digit'
                });
                return (
                  <Tab
                    key={`del-${item.data.uid}`}
                    item={item}
                    isActive={false}
                    onClick={() => handleTabClick(Tabs.indexOf(item))}
                    Tasks={Tasks}
                    isHidde={true}
                    time={timeString}
                  />
                );
              })}
            </>
          )}

          {categorizedOrders.pickup.length > 0 && (
            <>
              <div className='dropdown-header'>Самовивіз</div>
              {categorizedOrders.pickup.map((item) => {
                const timeString = (new Date(item.data.open) ?? new Date()).toLocaleTimeString('ru-RU', {
                  hour: '2-digit',
                  minute: '2-digit'
                });
                return (
                  <Tab
                    key={`pick-${item.data.uid}`}
                    item={item}
                    isActive={false}
                    onClick={() => handleTabClick(Tabs.indexOf(item))}
                    Tasks={Tasks}
                    isHidde={true}
                    time={timeString}
                  />
                );
              })}
            </>
          )}
          
        </div>
      )}
    </div>
  );
}

function TabPanel({ Tabs, Tasks, User, ActiveTab, TabClick, TabsMode }) {
  if (TabsMode !== '0') {
    return <AdvancedTabPanel Tabs={Tabs} Tasks={Tasks} User={User} ActiveTab={ActiveTab} TabClick={TabClick} />;
  } else {
    return <SimpleTabPanel Tabs={Tabs} Tasks={Tasks} ActiveTab={ActiveTab} TabClick={TabClick} />;
  }
}

export default React.memo(TabPanel);